import { useQuery, useReactiveVar } from "@apollo/client";
import { withErrorBoundary } from "@sentry/react";
import { useParams } from "react-router-dom";
import {
  AssessmentType,
  GetAssessmentSuggestedAnswerStateQuery,
  GetAssessmentSuggestedAnswerStateQueryVariables,
  SuggestedAnswerState,
} from "types/graphql-schema";

import { pendingChatPromptVar } from "@apps/ask-ai/ask-ai-context";
import getAssessmentSuggestedAnswerState from "@apps/assessments/graphql/get-assessment-suggested-answer-state";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentOrganizationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import ErrorFallback from "@components/error-fallback/error-fallback";
import Loading from "@components/loading/loading";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";

import { AssessmentSidebarTab } from "../assessment/types";
import { assessmentContextSidebarVar } from "../context-sidebar/context-sidebar-context";

const QuestionContextButtonComponent = ({
  questionId,
  title,
  assessmentType,
}: {
  questionId: number;
  title: string;
  assessmentType?: AssessmentType | null;
}) => {
  const params = useParams<{ assessmentId?: string }>();
  const assessmentId = params.assessmentId
    ? parseInt(params.assessmentId)
    : null;
  const assessmentContextSidebar = useReactiveVar(assessmentContextSidebarVar);
  const organization = currentOrganizationVar();
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();

  const { data } = useQuery<
    GetAssessmentSuggestedAnswerStateQuery,
    GetAssessmentSuggestedAnswerStateQueryVariables
  >(getAssessmentSuggestedAnswerState, {
    variables: { assessmentId: assessmentId || 0 },
    skip: !assessmentId,
  });

  const isPreparingContext =
    data?.assessment?.suggestedAnswersState !== SuggestedAnswerState.Generated;

  if (!assessmentId || !questionId || !organization.featureFlags.askAi) {
    return null;
  }
  if (!organization.featureFlags.reviewContextGeneration) {
    return null;
  }
  if (assessmentType === AssessmentType.EngagementSurvey) {
    return null;
  }
  if (!assessmentType) {
    if (import.meta.env.DEV) {
      return (
        <div className="bg-red-500 text-white text-lg font-medium">
          {questionId}) assessmentType: {assessmentType}
        </div>
      );
    }
    return null;
  }

  return (
    <div className="mt-2 hidden lg:flex justify-end">
      <Button
        onClick={() => {
          if (
            !uiPreferenceCache.assessmentSidebarExpanded ||
            uiPreferenceCache.assessmentSidebarSelectedTab !==
              AssessmentSidebarTab.askAI
          ) {
            saveUiPreference({
              assessmentSidebarExpanded: true,
              assessmentSidebarSelectedTab: AssessmentSidebarTab.askAI,
            });
          }
          assessmentContextSidebarVar({
            assessmentId,
            questionId,
          });
          pendingChatPromptVar({
            target: `review-${assessmentId}-question-${questionId}`,
            prompt: `Help me draft a response to this question: “${title}”`,
          });
        }}
        size={ButtonSize.medium}
        disabled={isPreparingContext}
        tooltip={
          isPreparingContext
            ? "Suggested response sources are still being prepared."
            : undefined
        }
        className={classNames(
          "js-test-generate-context-button",
          assessmentContextSidebar?.questionId === questionId
            ? "text-blue-link"
            : "",
        )}
      >
        {isPreparingContext ? (
          <Loading mini size="4" />
        ) : (
          <TopicflowIcon type="ai" className="size-3 text-gray-500" />
        )}
        {isPreparingContext ? "Preparing Context..." : "Draft with AI"}
      </Button>
    </div>
  );
};

const QuestionContextButton = withErrorBoundary(
  QuestionContextButtonComponent,
  {
    fallback: ErrorFallback,
  },
);

export default QuestionContextButton;
